import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import Button from "@/components/Common/Button";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import i18n from "@/i18n";
import {useManagementCampaign} from "@/store/campaignManagement";
import useTokenStore from "@/store/token";
import type {
  TCompanyGroup,
  TGroup,
  TGroupInfluencer,
  TInfiniteQueryOptions,
  TInfluencerProfileDetail,
  TQueryOptions,
  TQueryResultWithCount,
} from "@/store/types";
import {groupName} from "@/utils/groupUtils";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useParams} from "react-router";
import {toast, type Id} from "react-toastify";

export const GROUP_LIST_QUERY_CUSTOM_KEYS = [
  "in_group_badge",
  "company_profile",
  "group_selector",
  "influencer_network",
] as const;

export const GROUP_KEYS = {
  mGroup: (groupId: number) => [`/group/${groupId}/`],
  mGroupCampaignInvite: (groupId: number) => [`/group/${groupId}/campaign_invite/`],
  mGroupCampaignInviteInfluencers: (groupId: number) => [`/group/${groupId}/add_invite_influencers/`],
  mGroupCampaignInviteInfluencersRemoval: (groupId: number) => [`/group/${groupId}/remove_invite_influencers/`],
  mGroupCampaignInviteRemoval: (groupId: number) => [`/group/${groupId}/remove_campaign_invite/`],
  mGroupList: ["/group/"],
  mGroupMember: (groupId: number) => [`/group/${groupId}/add_influencer/`],
  mGroupMemberRemoval: (groupId: number) => [`/group/${groupId}/remove_influencer/`],
  qGroup: (groupId: number) => [`/group/${groupId}/`],
  qGroupCsvData: (groupId: number) => [`/group/${groupId}/csv_data/`],
  qGroupInfluencers: (groupId: number) => [`/group/${groupId}/influencers/`],
  qGroupList: (params?: string) => [`/group/?${params}`],
  qGroupSimilarInfluencers: (groupId: number) => [`/group/${groupId}/similar_influencers/`],
} as const;

export const useGroupListQueryParams = ({
  campaignId,
  companyId,
  excludeSmartGroups,
  searchParams = "",
  pageSize,
}: {
  campaignId?: number | null;
  companyId?: number | null;
  excludeSmartGroups?: boolean;
  searchParams?: string;
  pageSize?: number;
}) =>
  `${companyId ? `&company_id=${companyId}` : ""}${campaignId ? `&campaign_id=${campaignId}` : ""}${searchParams ? `&${searchParams}` : ""}${pageSize ? `&page_size=${pageSize}` : ""}${excludeSmartGroups ? "&smart=false" : ""}`;

export const useRemoveInvitedGroupFromCampaignMutation = ({campaignId}: {campaignId: number}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [`/campaign/${campaignId}/remove_campaign_invite/`], // TODO: it would be better to rewrite the endpoint like this
    mutationFn: (groupId: number) =>
      client.post(GROUP_KEYS.mGroupCampaignInviteRemoval(groupId)[0], {campaign_id: campaignId}),
    onSuccess: () => {
      [CAMPAIGN_KEYS.qCampaignInvitedGroups(campaignId), CAMPAIGN_KEYS.qCampaignInvitations(campaignId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });
};

export const useAddInfluencerToGroupMutation = ({
  influencer,
  group,
  showToastOnSuccess = false,
  onSuccessCallback,
  onUndoCallback,
}: {
  influencer: TInfluencerProfileDetail;
  group?: TCompanyGroup | TGroup | null;
  showToastOnSuccess?: boolean;
  onSuccessCallback?: () => void;
  onUndoCallback?: () => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = useSelectedCompanyId();
  const {refetch: refetchUser} = useFetchUser();
  let toastId: Id | null = null;

  return useMutation({
    mutationKey: GROUP_KEYS.mGroupMember(group?.id!),
    mutationFn: () => client.post(GROUP_KEYS.mGroupMember(group?.id!)[0], {influencer_id: influencer.uuid}),
    onSuccess: () => {
      if (showToastOnSuccess) {
        toastId = toast.info(
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2">
              {influencer.picture320 ? (
                <img
                  src={influencer.picture320}
                  className="border-neutral-grey-light-dark size-8 shrink-0 rounded-full border"
                />
              ) : (
                <div className="border-neutral-grey-light-dark bg-primary-light-solid size-8 shrink-0 rounded-full border" />
              )}
              <div className="text-neutral-black flex flex-col gap-0.5 text-xs">
                {i18n.t("savedTo")}
                <span
                  onClick={() => window.open(`/group/${group?.id}`, "_blank")}
                  className="font-semibold hover:cursor-pointer hover:underline">
                  {groupName(group!)}
                </span>
              </div>
            </div>
            <Button
              onClick={() => {
                onUndoCallback?.();
                if (toastId) toast.dismiss(toastId);
              }}
              buttonStyle="White"
              className="h-auto px-8 py-2">
              {i18n.t("undo")}
            </Button>
          </div>,
          {
            className: "w-fit rounded-md px-4 py-2 bg-white",
          },
        );
      }

      onSuccessCallback?.();
      [
        GROUP_KEYS.qGroupInfluencers(group?.id!),
        GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId})),
        GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId, excludeSmartGroups: true})),
        GROUP_KEYS.qGroupSimilarInfluencers(group?.id!),
        ...GROUP_LIST_QUERY_CUSTOM_KEYS.map(key => [
          ...GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId})),
          key,
        ]),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));

      refetchUser();

      if (group?.name === "favourite" && companyId) {
        client.post(COMPANY_KEYS.mCompanyFavouriteInfluencer(companyId)[0], {influencer_id: influencer.uuid});
      }
    },
  });
};

export const useRemoveInfluencerFromGroupMutation = ({
  influencer,
  group,
  showToastOnSuccess = false,
  onSuccessCallback,
  onUndoCallback,
}: {
  influencer: TInfluencerProfileDetail;
  group?: TCompanyGroup | TGroup | null;
  showToastOnSuccess?: boolean;
  onSuccessCallback?: () => void;
  onUndoCallback?: () => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = useSelectedCompanyId();
  const {refetch: refetchUser} = useFetchUser();
  let toastId: Id | null = null;

  return useMutation({
    mutationKey: GROUP_KEYS.mGroupMemberRemoval(group?.id!),
    mutationFn: () => client.post(GROUP_KEYS.mGroupMemberRemoval(group?.id!)[0], {influencer_id: influencer.uuid}),
    onSuccess: () => {
      if (showToastOnSuccess) {
        toastId = toast.info(
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2">
              {influencer.picture320 ? (
                <img
                  src={influencer.picture320}
                  className="border-neutral-grey-light-dark size-8 shrink-0 rounded-full border"
                />
              ) : (
                <div className="border-neutral-grey-light-dark bg-primary-light-solid size-8 shrink-0 rounded-full border" />
              )}
              <div className="text-neutral-black flex flex-col gap-0.5 text-xs">
                {i18n.t("removedFrom")}
                <span
                  onClick={() => window.open(`/group/${group?.id}`, "_blank")}
                  className="font-semibold hover:cursor-pointer hover:underline">
                  {groupName(group!)}
                </span>
              </div>
            </div>
            <Button
              onClick={() => {
                onUndoCallback?.();
                if (toastId) toast.dismiss(toastId);
              }}
              buttonStyle="White"
              className="h-auto px-8 py-2">
              {i18n.t("undo")}
            </Button>
          </div>,
          {
            className: "w-fit rounded-md px-4 py-2 bg-white",
          },
        );
      }

      onSuccessCallback?.();
      [
        GROUP_KEYS.qGroupInfluencers(group?.id!),
        GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId})),
        GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId, excludeSmartGroups: true})),
        GROUP_KEYS.qGroupSimilarInfluencers(group?.id!),
        ...GROUP_LIST_QUERY_CUSTOM_KEYS.map(key => [
          ...GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId})),
          key,
        ]),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));

      refetchUser();

      if (group?.name === "favourite" && companyId) {
        client.post(COMPANY_KEYS.mCompanyFavouriteInfluencer(companyId)[0], {influencer_id: influencer.uuid});
      }
    },
  });
};

export const useGroupQuery = ({groupId, queryOptions}: {groupId?: number; queryOptions?: TQueryOptions<TGroup>}) =>
  useGenericQuery<TGroup>({
    queryKey: GROUP_KEYS.qGroup(groupId!),
    enabled: !!groupId,
    ...queryOptions,
  });

export const useGroupInfluencersQuery = ({
  groupId,
  queryOptions,
}: {
  groupId?: number;
  queryOptions?: TInfiniteQueryOptions<TGroupInfluencer, AxiosError, TQueryResultWithCount<TGroupInfluencer[]>>;
}) =>
  useGenericInfiniteQueryWithCount({
    queryKey: GROUP_KEYS.qGroupInfluencers(groupId!),
    enabled: !!groupId,
    ...queryOptions,
  });

export const useGroupListQuery = ({
  campaignId,
  searchParams,
  queryOptions,
}: {
  campaignId?: number;
  searchParams?: string;
  queryOptions?: TInfiniteQueryOptions<TCompanyGroup>;
}) => {
  const {companyId: companyIdFromUrl} = useParams();
  const managedCampaign = useManagementCampaign();
  const selectedCompanyId = useSelectedCompanyId();
  const companyId = companyIdFromUrl ?? selectedCompanyId ?? managedCampaign.company_profile?.id;

  return useGenericInfiniteQuery<TCompanyGroup>({
    queryKey: GROUP_KEYS.qGroupList(
      useGroupListQueryParams({campaignId, companyId: companyId ? +companyId : undefined, searchParams}),
    ),
    enabled: !!useTokenStore.getState().userId,
    ...queryOptions,
  });
};
